/**
 * ReaderToolbar Component
 *
 * Header and footer controls for the Sefaria reader:
 * - Header: Title, close button, audio icon, verse favorites, settings
 * - Footer: Layout toggles, font size, position indicator
 */

import { memo } from 'react';
import {
  X,
  Settings,
  AlignLeft,
  Columns,
  Type,
  ChevronUp,
  ChevronDown,
  Minus,
  Plus,
} from 'lucide-react';
import { HeaderAudioIcon } from '@/components/HeaderAudioIcon';
import { VerseFavoritesDropdown } from './VerseFavoritesDropdown';
import type { ReaderToolbarProps, ReaderLayout, ReaderFontSize } from '@/types/sefaria';

// ============================================================
// Layout Icons
// ============================================================

const LAYOUT_ICONS: Record<ReaderLayout, React.ReactNode> = {
  stacked: <AlignLeft size={18} />,
  'side-by-side': <Columns size={18} />,
  'hebrew-only': (
    <span className="text-sm font-bold" dir="rtl">
      עב
    </span>
  ),
  'english-only': <span className="text-sm font-bold">En</span>,
};

const LAYOUT_LABELS: Record<ReaderLayout, string> = {
  stacked: 'Stacked',
  'side-by-side': 'Side by Side',
  'hebrew-only': 'Hebrew Only',
  'english-only': 'English Only',
};

// ============================================================
// Header Component
// ============================================================

interface HeaderProps {
  title: string;
  hebrewTitle: string;
  currentParshaRef: string;
  onOpenSettings: () => void;
  onClose: () => void;
  onJumpToVerse: (verseRef: string) => void;
  onOpenDifferentParsha: (
    parshaRef: string,
    parshaTitle: string,
    parshaHebrew: string,
    focusVerseRef: string
  ) => void;
}

function Header({
  title,
  hebrewTitle,
  currentParshaRef,
  onOpenSettings,
  onClose,
  onJumpToVerse,
  onOpenDifferentParsha,
}: HeaderProps) {
  return (
    <header className="bg-[#63345e] text-white px-4 py-3 flex items-center justify-between shrink-0 safe-area-top">
      {/* Close button */}
      <button
        onClick={onClose}
        className="p-2 hover:bg-[#8e4585] rounded-lg transition-colors"
        aria-label="Close reader"
      >
        <X size={24} />
      </button>

      {/* Title */}
      <div className="flex-1 text-center mx-4">
        <h1 className="font-bold text-lg truncate">{title}</h1>
        {hebrewTitle && (
          <h2 className="text-[#f0d2f0] text-sm" dir="rtl" lang="he">
            {hebrewTitle}
          </h2>
        )}
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-1">
        {/* Audio player icon */}
        <HeaderAudioIcon />

        {/* Verse favorites dropdown */}
        <VerseFavoritesDropdown
          currentParshaRef={currentParshaRef}
          onJumpToVerse={onJumpToVerse}
          onOpenDifferentParsha={onOpenDifferentParsha}
        />

        {/* Settings button */}
        <button
          onClick={onOpenSettings}
          className="p-2 hover:bg-[#8e4585] rounded-lg transition-colors"
          aria-label="Open settings"
        >
          <Settings size={22} />
        </button>
      </div>
    </header>
  );
}

// ============================================================
// Footer Component
// ============================================================

interface FooterProps {
  layout: ReaderLayout;
  fontSize: ReaderFontSize;
  currentVerse: number;
  totalVerses: number;
  onLayoutChange: (layout: ReaderLayout) => void;
  onFontSizeChange: (size: ReaderFontSize) => void;
  onScrollToTop: () => void;
  onScrollToBottom: () => void;
}

const FONT_SIZES: ReaderFontSize[] = ['small', 'medium', 'large', 'xlarge'];

function Footer({
  layout,
  fontSize,
  currentVerse,
  totalVerses,
  onLayoutChange,
  onFontSizeChange,
  onScrollToTop,
  onScrollToBottom,
}: FooterProps) {
  const layouts: ReaderLayout[] = ['stacked', 'side-by-side', 'hebrew-only', 'english-only'];
  const currentFontIndex = FONT_SIZES.indexOf(fontSize);

  const decreaseFontSize = () => {
    if (currentFontIndex > 0) {
      onFontSizeChange(FONT_SIZES[currentFontIndex - 1]);
    }
  };

  const increaseFontSize = () => {
    if (currentFontIndex < FONT_SIZES.length - 1) {
      onFontSizeChange(FONT_SIZES[currentFontIndex + 1]);
    }
  };

  return (
    <footer className="bg-white border-t border-slate-200 px-4 py-2 shrink-0 safe-area-bottom">
      <div className="flex items-center justify-between gap-2">
        {/* Layout toggles */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {layouts.map((l) => (
            <button
              key={l}
              onClick={() => onLayoutChange(l)}
              className={`
                p-2 rounded-md transition-colors
                ${layout === l ? 'bg-[#8e4585] text-white' : 'text-slate-500 hover:bg-slate-200'}
              `}
              title={LAYOUT_LABELS[l]}
              aria-label={`Layout: ${LAYOUT_LABELS[l]}`}
              aria-pressed={layout === l}
            >
              {LAYOUT_ICONS[l]}
            </button>
          ))}
        </div>

        {/* Font size controls */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          <button
            onClick={decreaseFontSize}
            disabled={currentFontIndex === 0}
            className={`
              p-2 rounded-md transition-colors
              ${currentFontIndex === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-500 hover:bg-slate-200'}
            `}
            aria-label="Decrease font size"
          >
            <Minus size={16} />
          </button>
          <div className="px-2 text-slate-500">
            <Type size={18} />
          </div>
          <button
            onClick={increaseFontSize}
            disabled={currentFontIndex === FONT_SIZES.length - 1}
            className={`
              p-2 rounded-md transition-colors
              ${currentFontIndex === FONT_SIZES.length - 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-500 hover:bg-slate-200'}
            `}
            aria-label="Increase font size"
          >
            <Plus size={16} />
          </button>
        </div>

        {/* Position indicator */}
        <div className="text-sm text-slate-500 min-w-[100px] text-center">
          {currentVerse > 0 && totalVerses > 0 && (
            <span>
              {currentVerse} / {totalVerses}
            </span>
          )}
        </div>

        {/* Jump buttons */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          <button
            onClick={onScrollToTop}
            className="p-2 rounded-md text-slate-500 hover:bg-slate-200 transition-colors"
            aria-label="Scroll to top"
          >
            <ChevronUp size={18} />
          </button>
          <button
            onClick={onScrollToBottom}
            className="p-2 rounded-md text-slate-500 hover:bg-slate-200 transition-colors"
            aria-label="Scroll to bottom"
          >
            <ChevronDown size={18} />
          </button>
        </div>
      </div>
    </footer>
  );
}

// ============================================================
// Main Component
// ============================================================

function ReaderToolbarComponent(props: ReaderToolbarProps) {
  return (
    <>
      <Header
        title={props.title}
        hebrewTitle={props.hebrewTitle}
        currentParshaRef={props.currentParshaRef}
        onOpenSettings={props.onOpenSettings}
        onClose={props.onClose}
        onJumpToVerse={props.onJumpToVerse}
        onOpenDifferentParsha={props.onOpenDifferentParsha}
      />
    </>
  );
}

// Export both header and footer separately for flexible use
export const ReaderHeader = memo(Header);
export const ReaderFooter = memo(Footer);
export const ReaderToolbar = memo(ReaderToolbarComponent);
